/**
 * Every way into this account, and how to change the password.
 *
 * Listing the methods matters more than it looks: an account created through Google has no
 * password at all, and until it is told that, "I forgot my password" is a question with no
 * answer. So the panel says which routes exist, and offers to add the missing one.
 */
import { useState } from 'react'

import { Field, FormNote } from '@/components/profile/Field'
import { Button } from '@/components/ui/Controls'
import { Panel } from '@/components/ui/Panel'
import { useChangePassword } from '@/features/profile/useProfile'
import { toApiError } from '@/lib/api/errors'
import { formatTimestamp, titleCase } from '@/lib/utils/format'

import type { components } from '@/types/generated/api'

type Profile = components['schemas']['ProfileResponse']

const MIN_PASSWORD_LENGTH = 10

function methodLabel(provider: string): string {
  return provider === 'password' ? 'Email and password' : `${titleCase(provider)} sign-in`
}

export function SignInMethodsPanel({ profile }: { profile: Profile }) {
  const change = useChangePassword()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [outcome, setOutcome] = useState<string | null>(null)

  const error = change.error ? toApiError(change.error) : null
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH

  return (
    <Panel
      title="How you sign in"
      description={profile.has_password ? undefined : 'This account has no password yet'}
    >
      <ul className="border-b border-line/60">
        {(profile.sign_in_methods ?? []).map((method) => (
          <li
            key={method.provider}
            className="flex items-center justify-between gap-3 border-b border-line/60 px-4 py-2.5 last:border-b-0"
          >
            <span className="min-w-0">
              <span className="block text-sm text-ink-dim">{methodLabel(method.provider)}</span>
              <span className="mono block truncate text-xs text-ink-faint">
                {method.email ?? profile.email ?? '—'}
              </span>
            </span>
            <span className="shrink-0 text-xs text-ink-faint">
              {method.last_used_at
                ? `Last used ${formatTimestamp(method.last_used_at)}`
                : method.linked_at
                  ? `Linked ${formatTimestamp(method.linked_at)}`
                  : 'Active'}
            </span>
          </li>
        ))}
      </ul>

      <form
        className="space-y-3 px-4 py-4"
        onSubmit={(event) => {
          event.preventDefault()
          setOutcome(null)
          change.mutate(
            {
              current_password: profile.has_password ? current : null,
              new_password: next,
            },
            {
              onSuccess: (result) => {
                setCurrent('')
                setNext('')
                setOutcome(
                  result.other_sessions_revoked > 0
                    ? `Password changed. ${result.other_sessions_revoked} other session${
                        result.other_sessions_revoked === 1 ? ' was' : 's were'
                      } signed out.`
                    : 'Password changed.',
                )
              },
            },
          )
        }}
      >
        {profile.has_password ? (
          <Field
            label="Current password"
            type="password"
            value={current}
            onChange={setCurrent}
            autoComplete="current-password"
          />
        ) : null}
        <Field
          label={profile.has_password ? 'New password' : 'Choose a password'}
          type="password"
          value={next}
          onChange={setNext}
          autoComplete="new-password"
          hint={
            profile.has_password
              ? `At least ${MIN_PASSWORD_LENGTH} characters. Changing it signs out your other devices.`
              : `At least ${MIN_PASSWORD_LENGTH} characters. You will still be able to sign in with Google.`
          }
        />
        <div className="flex items-center gap-3">
          <Button
            type="submit"
            variant="primary"
            disabled={change.isPending || next.length < MIN_PASSWORD_LENGTH}
          >
            {change.isPending
              ? 'Saving…'
              : profile.has_password
                ? 'Change password'
                : 'Set password'}
          </Button>
          {tooShort ? (
            <span className="text-xs text-ink-muted">
              {MIN_PASSWORD_LENGTH - next.length} more characters
            </span>
          ) : null}
        </div>
        {outcome ? <FormNote tone="ok">{outcome}</FormNote> : null}
        {error ? <FormNote tone="error">{error.message}</FormNote> : null}
      </form>
    </Panel>
  )
}
